import { isAxiosError } from 'axios';
import { makeAutoObservable, runInAction } from 'mobx';

import api, { getErrorMessage } from '../api';
import { IPlayerInfo } from '../types/player';
import { IStatus } from '../types/status';
import { ITemporaryCredentials } from '../types/user';

type IRandomError = 'minimal' | 'network';

type RandomedTeams = {
    teamRadiant: IPlayerInfo[];
    teamDire: IPlayerInfo[];
};

export type NewPlayer = Pick<
    IPlayerInfo,
    'fullName' | 'nickname' | 'accountId'
> & {
    /** Логин аккаунта; пустой — сервер сделает его из ника */
    login?: string;
};

/** Созданный игрок вместе с данными для первого входа */
export type CreatedPlayer = IPlayerInfo & { account: ITemporaryCredentials };

const validationMessages: Record<string, string> = {
    fullName: 'Имя должно быть длиной от 3 до 16 букв.',
    accountId: 'accountId игрока должен состоять из 9 цифр.',
    nickname: 'Укажите nickname игрока.',
    login: 'Логин: 3–32 символа, латиница, цифры, точка, _ или -.',
};

class Player {
    /** Все игроки. Не мутируется на месте — только заменяется целиком */
    playerList: IPlayerInfo[] = [];
    /** id игроков, выбранных в генераторе команд, в порядке выбора */
    pickedIds: IPlayerInfo['_id'][] = [];
    randomedTeams: RandomedTeams = {
        teamRadiant: [],
        teamDire: [],
    };

    fetchStatus: IStatus = 'idle';
    isSteamUpdating = false;

    randomStatus: IStatus = 'idle';
    randomError: IRandomError | null = null;

    currentPlayer: IPlayerInfo | null = null;
    currentPlayerStatus: IStatus = 'idle';

    constructor() {
        makeAutoObservable(this);
    }

    get playersById() {
        return new Map(this.playerList.map((p) => [p._id, p]));
    }

    /** После смены логина: адрес профиля меняется без перезагрузки */
    applyLogin(playerId: string, login: string) {
        if (this.currentPlayer?._id === playerId) {
            this.currentPlayer = { ...this.currentPlayer, login };
        }
        this.playerList = this.playerList.map((p) =>
            p._id === playerId ? { ...p, login } : p
        );
    }

    /** Выбранные в генераторе игроки (в порядке выбора) */
    get pickedPlayers() {
        return this.pickedIds
            .map((id) => this.playersById.get(id))
            .filter((p): p is IPlayerInfo => !!p);
    }

    /** Игроки, которых ещё можно добавить в генератор */
    get availablePlayers() {
        const picked = new Set(this.pickedIds);
        return this.playerList.filter((p) => !picked.has(p._id));
    }

    pickPlayer(id: IPlayerInfo['_id']) {
        if (!this.pickedIds.includes(id)) {
            this.pickedIds = [...this.pickedIds, id];
        }
    }

    unpickPlayer(id: IPlayerInfo['_id']) {
        this.pickedIds = this.pickedIds.filter((pickedId) => pickedId !== id);
    }

    clearPicked() {
        this.pickedIds = [];
    }

    async fetchPlayers() {
        this.fetchStatus = 'pending';
        try {
            const { data } = await api.get<IPlayerInfo[]>('/players');
            runInAction(() => {
                this.playerList = data;
                this.fetchStatus = 'fulfilled';
            });
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this.fetchStatus = 'rejected';
            });
        }
    }

    /** Подходит ли игрок под адрес /players/<id или логин> */
    matchesSlug(player: IPlayerInfo | null, slug: string) {
        return !!player && (player._id === slug || player.login === slug);
    }

    /** id или логин игрока */
    async fetchPlayerById(id: string) {
        if (!this.matchesSlug(this.currentPlayer, id)) {
            this.currentPlayer = null;
        }
        this.currentPlayerStatus = 'pending';
        try {
            const { data } = await api.get<IPlayerInfo>('/players/' + id);
            runInAction(() => {
                this.currentPlayer = data;
                this.currentPlayerStatus = 'fulfilled';
            });
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this.currentPlayerStatus = 'rejected';
            });
        }
    }

    /** Создаёт игрока. Бросает Error с понятным текстом при неудаче. */
    async createPlayer(player: NewPlayer) {
        try {
            const { data } = await api.put<CreatedPlayer>('players', player);
            const { account: _account, ...created } = data;
            runInAction(() => {
                this.playerList = [...this.playerList, created];
            });
            return data;
        } catch (err) {
            if (isAxiosError(err) && err.response?.status === 409) {
                throw new Error(getErrorMessage(err));
            }
            const field = isAxiosError(err)
                ? (err.response?.data?.[0]?.path ??
                  err.response?.data?.[0]?.param)
                : undefined;
            throw new Error(
                validationMessages[field] ?? 'Не удалось создать игрока.'
            );
        }
    }

    /**
     * Меняет ник (сам игрок или админ) и имя (только админ).
     * Бросает Error с понятным текстом при неудаче.
     */
    async updatePlayer(
        id: string,
        changes: Partial<Pick<IPlayerInfo, 'nickname' | 'fullName'>>
    ) {
        try {
            const { data } = await api.patch<IPlayerInfo>(
                '/players/' + id,
                changes
            );
            runInAction(() => {
                if (this.currentPlayer?._id === id) this.currentPlayer = data;
                this.replacePlayer(data);
            });
            return data;
        } catch (err) {
            // Ошибки валидации приходят массивом: [{ msg, path }]
            const validationMessage = isAxiosError(err)
                ? err.response?.data?.[0]?.msg
                : undefined;
            throw new Error(
                validationMessage ??
                    getErrorMessage(err, 'Не удалось сохранить изменения.')
            );
        }
    }

    randomPlayers = async () => {
        const players = this.pickedPlayers;
        const amount = players.length;

        if (amount === 0 || amount % 2 !== 0) {
            this.randomStatus = 'rejected';
            this.randomError = 'minimal';
            return;
        }

        this.randomStatus = 'pending';
        this.randomError = null;

        try {
            const response = await api.post('/random', { amount });
            // random.org возвращает перестановку индексов 1..amount
            const order: number[] = response.data.result.random.data[0];
            const shuffled = order.map((n) => players[n - 1]);

            runInAction(() => {
                this.randomedTeams = {
                    teamRadiant: shuffled.slice(0, amount / 2),
                    teamDire: shuffled.slice(amount / 2),
                };
                this.randomStatus = 'fulfilled';
            });
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this.randomStatus = 'rejected';
                this.randomError = 'network';
            });
        }
    };

    private replacePlayer(player: IPlayerInfo) {
        this.playerList = this.playerList.map((p) =>
            p._id === player._id ? player : p
        );
    }

    async updatePlayerInfoFromSteam(accountId: string) {
        this.isSteamUpdating = true;
        try {
            const { data } = await api.get<IPlayerInfo>(
                '/steam/players/' + accountId
            );
            runInAction(() => {
                this.currentPlayer = data;
                this.replacePlayer(data);
            });
        } finally {
            runInAction(() => {
                this.isSteamUpdating = false;
            });
        }
    }

    async updateEveryPlayerInfoFromSteam() {
        this.isSteamUpdating = true;
        try {
            const { data } = await api.get<IPlayerInfo[]>('/steam/players');
            runInAction(() => {
                this.playerList = data;
            });
        } finally {
            runInAction(() => {
                this.isSteamUpdating = false;
            });
        }
    }
}

const playerStore = new Player();
export default playerStore;
